import { create } from "zustand";
import {
  acceptBriefInvite,
  createBoard,
  createCard,
  createBriefCategory,
  createList,
  createProject,
  createWorkspace,
  updateWorkspace,
  deleteBoard,
  deleteBriefCategory,
  listBriefCategories,
  updateBriefCategory,
  deleteCard,
  deleteList,
  getBoardSnapshot,
  listBoards,
  listProjects,
  listWorkspaces,
  moveCard,
  orderBetween,
  updateCard,
  updateList,
  type BriefBoard,
  type BriefCard,
  type BriefCategory,
  type BriefList,
  type BriefProject,
  type BriefWorkspace,
  type UpdateCardInput,
} from "@slumbr/core";

interface BriefState {
  workspaces: BriefWorkspace[];
  activeWorkspaceId: string | null;
  projects: BriefProject[];
  boards: BriefBoard[];
  // Active board snapshot
  board: BriefBoard | null;
  lists: BriefList[];
  cards: BriefCard[];
  categories: BriefCategory[];
  loadingWorkspaces: boolean;
  loadingBoard: boolean;
  error: string | null;

  loadWorkspaces: () => Promise<void>;
  setActiveWorkspace: (id: string) => Promise<void>;
  addWorkspace: (name: string) => Promise<BriefWorkspace | null>;
  updateWorkspaceName: (id: string, name: string) => Promise<void>;
  joinViaInvite: (token: string) => Promise<BriefWorkspace | null>;

  loadProjects: (wsId: string) => Promise<void>;
  addProject: (wsId: string, name: string) => Promise<BriefProject | null>;
  loadBoards: (projectId: string) => Promise<void>;
  addBoard: (projectId: string, name: string, templateLists?: string[]) => Promise<BriefBoard | null>;
  removeBoard: (id: string) => Promise<void>;

  loadBoard: (boardId: string) => Promise<void>;
  addList: (name: string) => Promise<void>;
  renameList: (id: string, name: string) => Promise<void>;
  removeList: (id: string) => Promise<void>;
  addCard: (listId: string, title: string) => Promise<void>;
  patchCard: (id: string, data: UpdateCardInput) => Promise<void>;
  removeCard: (id: string) => Promise<void>;
  dragCard: (cardId: string, toListId: string, beforeCardId?: string) => Promise<void>;

  addCategory: (data: { name: string; color: string; icon: string }) => Promise<void>;
  editCategory: (id: string, data: { name?: string; color?: string; icon?: string }) => Promise<void>;
  removeCategory: (id: string) => Promise<void>;

  applyRealtimeEvent: (event: unknown) => void;
}

/** Order key to append after the last item in a list. */
function appendOrder(items: { order: string }[]): string {
  const sorted = [...items].map((i) => i.order).sort();
  return orderBetween(sorted[sorted.length - 1], undefined);
}

export const useBriefStore = create<BriefState>((set, get) => ({
  workspaces: [],
  activeWorkspaceId: null,
  projects: [],
  boards: [],
  board: null,
  lists: [],
  cards: [],
  categories: [],
  loadingWorkspaces: true,
  loadingBoard: false,
  error: null,

  loadWorkspaces: async () => {
    set({ loadingWorkspaces: true, error: null });
    try {
      const workspaces = await listWorkspaces();
      const active = get().activeWorkspaceId ?? workspaces[0]?.id ?? null;
      set({ workspaces, activeWorkspaceId: active, loadingWorkspaces: false });
      if (active) await get().loadProjects(active);
    } catch (e) {
      set({ loadingWorkspaces: false, error: e instanceof Error ? e.message : "Failed to load" });
    }
  },

  setActiveWorkspace: async (id) => {
    set({ activeWorkspaceId: id, projects: [], boards: [] });
    await get().loadProjects(id);
  },

  addWorkspace: async (name) => {
    try {
      const ws = await createWorkspace({ name });
      set((s) => ({ workspaces: [...s.workspaces, ws], activeWorkspaceId: ws.id, projects: [] }));
      return ws;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to create workspace" });
      return null;
    }
  },

  updateWorkspaceName: async (id, name) => {
    set((s) => ({ workspaces: s.workspaces.map((w) => (w.id === id ? { ...w, name } : w)) }));
    try { await updateWorkspace(id, { name }); } catch { /* best-effort */ }
  },

  joinViaInvite: async (token) => {
    try {
      const ws = await acceptBriefInvite(token);
      await get().loadWorkspaces();
      set({ activeWorkspaceId: ws.id });
      return ws;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to join workspace" });
      return null;
    }
  },

  loadProjects: async (wsId) => {
    try {
      const projects = await listProjects(wsId);
      set({ projects });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load projects" });
    }
  },

  addProject: async (wsId, name) => {
    try {
      const project = await createProject(wsId, { name });
      set((s) => ({ projects: [...s.projects, project] }));
      return project;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to create project" });
      return null;
    }
  },

  loadBoards: async (projectId) => {
    try {
      const boards = await listBoards(projectId);
      set({ boards });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load boards" });
    }
  },

  addBoard: async (projectId, name, templateLists) => {
    try {
      const board = await createBoard(projectId, { name });
      set((s) => ({ boards: [...s.boards, board] }));
      // Seed the chosen template, or a default Kanban so the board is usable.
      const defaults = templateLists && templateLists.length > 0 ? templateLists : ["To do", "In progress", "Done"];
      let prev: string | undefined;
      for (const listName of defaults) {
        const order = orderBetween(prev, undefined);
        prev = order;
        try { await createList(board.id, { name: listName, order }); } catch { /* best-effort seed */ }
      }
      return board;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to create board" });
      return null;
    }
  },

  removeBoard: async (id) => {
    set((s) => ({ boards: s.boards.filter((b) => b.id !== id) }));
    try { await deleteBoard(id); } catch { /* best-effort */ }
  },

  loadBoard: async (boardId) => {
    set({ loadingBoard: true, error: null });
    try {
      const snap = await getBoardSnapshot(boardId);
      set({ board: snap.board, lists: snap.lists, cards: snap.cards, categories: snap.categories ?? [], loadingBoard: false });
    } catch (e) {
      set({ loadingBoard: false, error: e instanceof Error ? e.message : "Failed to load board" });
    }
  },

  addList: async (name) => {
    const board = get().board;
    if (!board) return;
    const order = appendOrder(get().lists);
    try {
      const list = await createList(board.id, { name, order });
      set((s) => ({ lists: [...s.lists, list] }));
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to add list" });
    }
  },

  renameList: async (id, name) => {
    set((s) => ({ lists: s.lists.map((l) => (l.id === id ? { ...l, name } : l)) }));
    try { await updateList(id, { name }); } catch { /* best-effort */ }
  },

  removeList: async (id) => {
    set((s) => ({ lists: s.lists.filter((l) => l.id !== id), cards: s.cards.filter((c) => c.listId !== id) }));
    try { await deleteList(id); } catch { /* best-effort */ }
  },

  addCard: async (listId, title) => {
    const board = get().board;
    if (!board) return;
    const inList = get().cards.filter((c) => c.listId === listId);
    const order = appendOrder(inList);
    try {
      const card = await createCard(board.id, { listId, title, order });
      set((s) => ({ cards: [...s.cards, card] }));
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to add card" });
    }
  },

  patchCard: async (id, data) => {
    // Optimistic.
    set((s) => ({ cards: s.cards.map((c) => (c.id === id ? { ...c, ...stripNulls(data as Record<string, unknown>) } : c)) }));
    try {
      const updated = await updateCard(id, data);
      set((s) => ({ cards: s.cards.map((c) => (c.id === id ? updated : c)) }));
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to update card" });
    }
  },

  removeCard: async (id) => {
    set((s) => ({ cards: s.cards.filter((c) => c.id !== id) }));
    try { await deleteCard(id); } catch { /* best-effort */ }
  },

  dragCard: async (cardId, toListId, beforeCardId) => {
    const cards = get().cards;
    const target = cards.filter((c) => c.listId === toListId && c.id !== cardId).sort((a, b) => (a.order < b.order ? -1 : 1));
    let order: string;
    if (!beforeCardId) {
      order = orderBetween(target[target.length - 1]?.order, undefined);
    } else {
      const idx = target.findIndex((c) => c.id === beforeCardId);
      const prev = target[idx - 1]?.order;
      const next = target[idx]?.order;
      order = orderBetween(prev, next);
    }
    set((s) => ({ cards: s.cards.map((c) => (c.id === cardId ? { ...c, listId: toListId, order } : c)) }));
    try { await moveCard(cardId, { listId: toListId, order }); } catch { /* best-effort */ }
  },

  addCategory: async (data) => {
    const board = get().board;
    if (!board) return;
    try { const cat = await createBriefCategory(board.id, data); set((s) => ({ categories: [...s.categories, cat] })); }
    catch (e) { set({ error: e instanceof Error ? e.message : "Failed to add category" }); }
  },
  editCategory: async (id, data) => {
    set((s) => ({ categories: s.categories.map((c) => (c.id === id ? { ...c, ...data } : c)) }));
    try { await updateBriefCategory(id, data); } catch { /* best-effort */ }
  },
  removeCategory: async (id) => {
    set((s) => ({
      categories: s.categories.filter((c) => c.id !== id),
      cards: s.cards.map((c) => (c.categoryId === id ? { ...c, categoryId: undefined } : c)),
    }));
    try { await deleteBriefCategory(id); } catch { /* best-effort */ }
  },

  applyRealtimeEvent: (event) => {
    const ev = event as { type?: string; boardId?: string } & Record<string, unknown>;
    if (!ev?.type || !ev.type.startsWith("brief.")) return;
    const board = get().board;
    if (!board || ev.boardId !== board.id) return;
    switch (ev.type) {
      case "brief.list.created":
        set((s) => (s.lists.some((l) => l.id === (ev.list as BriefList).id) ? s : { lists: [...s.lists, ev.list as BriefList] }));
        break;
      case "brief.list.updated":
        set((s) => ({ lists: s.lists.map((l) => (l.id === (ev.list as BriefList).id ? (ev.list as BriefList) : l)) }));
        break;
      case "brief.list.deleted":
        set((s) => ({ lists: s.lists.filter((l) => l.id !== ev.listId), cards: s.cards.filter((c) => c.listId !== ev.listId) }));
        break;
      case "brief.card.created":
        set((s) => (s.cards.some((c) => c.id === (ev.card as BriefCard).id) ? s : { cards: [...s.cards, ev.card as BriefCard] }));
        break;
      case "brief.card.updated":
        set((s) => ({ cards: s.cards.map((c) => (c.id === (ev.card as BriefCard).id ? (ev.card as BriefCard) : c)) }));
        break;
      case "brief.card.moved":
        set((s) => ({
          cards: s.cards.map((c) =>
            c.id === ev.cardId ? { ...c, listId: ev.listId as string, order: ev.order as string } : c
          ),
        }));
        break;
      case "brief.card.deleted":
        set((s) => ({ cards: s.cards.filter((c) => c.id !== ev.cardId) }));
        break;
      case "brief.category.changed":
        void listBriefCategories(board.id).then((cats) => set({ categories: cats })).catch(() => undefined);
        break;
    }
  },
}));

/** Drop null-valued keys (clears) for optimistic merge — server is authority. */
function stripNulls(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) if (v !== null) out[k] = v;
  return out;
}
